import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCallStore } from '@/stores/call-store';
import { useAuthStore } from '@/stores/auth-store';
import { getSocket } from '@/services/socket';
import '@/styles/call.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Full-screen call overlay. Renders when `current` in call-store is non-null.
 * Handles both incoming (ringing → accept/reject) and outgoing (ringing →
 * connecting → connected) flows in one component.
 */
export function CallScreen() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const current = useCallStore((s) => s.current);
  const localStream = useCallStore((s) => s.localStream);
  const remoteStream = useCallStore((s) => s.remoteStream);
  const muted = useCallStore((s) => s.muted);
  const videoOff = useCallStore((s) => s.videoOff);
  const accept = useCallStore((s) => s.accept);
  const reject = useCallStore((s) => s.reject);
  const hangup = useCallStore((s) => s.hangup);
  const setMuted = useCallStore((s) => s.setMuted);
  const setVideoOff = useCallStore((s) => s.setVideoOff);

  const localVid = useRef<HTMLVideoElement | null>(null);
  const remoteVid = useRef<HTMLVideoElement | null>(null);
  const remoteAudio = useRef<HTMLAudioElement | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (localVid.current && localStream) localVid.current.srcObject = localStream;
  }, [localStream]);
  useEffect(() => {
    if (remoteVid.current && remoteStream) remoteVid.current.srcObject = remoteStream;
    if (remoteAudio.current && remoteStream) remoteAudio.current.srcObject = remoteStream;
  }, [remoteStream]);

  // Call duration ticker — start when state becomes "connected"
  useEffect(() => {
    if (current?.state !== 'connected') {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [current?.state]);

  if (!current) return null;

  const isVideo = current.kind === 'video';
  const isRinging = current.state === 'ringing';
  const isIncoming = current.direction === 'incoming';

  const statusText =
    current.state === 'ringing'
      ? isIncoming
        ? t('call.state_incoming')
        : t('call.state_calling')
      : current.state === 'connecting'
        ? t('call.state_connecting')
        : current.state === 'connected'
          ? fmtTime(elapsed)
          : t('call.state_ended');

  async function onAccept() {
    if (!user) return;
    const socket = await getSocket();
    await accept(socket, user.id);
  }

  async function onReject() {
    if (!user) return;
    const socket = await getSocket();
    reject(socket, user.id);
  }

  return (
    <div className="call-screen" data-kind={current.kind} data-state={current.state}>
      {/* Video layer */}
      {isVideo && (
        <>
          <video
            ref={remoteVid}
            className="call-remote-video"
            autoPlay
            playsInline
          />
          {!videoOff && (
            <video
              ref={localVid}
              className="call-local-video"
              autoPlay
              playsInline
              muted
            />
          )}
        </>
      )}

      {/* Audio-only: invisible audio element attached to remote stream */}
      {!isVideo && <audio ref={remoteAudio} autoPlay playsInline />}

      {/* Caller info overlay (always on top; dims for video when connected) */}
      <div className="call-caller">
        <div className="call-avatar">{initials(current.remoteUserName)}</div>
        <div className="call-name">{current.remoteUserName}</div>
        <div className="call-status">{statusText}</div>
      </div>

      {/* Actions */}
      <div className="call-actions">
        {isIncoming && isRinging ? (
          <>
            <button
              type="button"
              className="call-btn call-btn-reject"
              onClick={onReject}
              aria-label={t('call.reject')}
            >
              <span className="material-symbols-outlined">call_end</span>
            </button>
            <button
              type="button"
              className="call-btn call-btn-accept"
              onClick={onAccept}
              aria-label={t('call.accept')}
            >
              <span className="material-symbols-outlined">{isVideo ? 'videocam' : 'call'}</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`call-btn call-btn-toggle${muted ? ' active' : ''}`}
              onClick={() => setMuted(!muted)}
              aria-label={muted ? t('call.unmute') : t('call.mute')}
              aria-pressed={muted}
            >
              <span className="material-symbols-outlined">{muted ? 'mic_off' : 'mic'}</span>
            </button>

            {isVideo && (
              <button
                type="button"
                className={`call-btn call-btn-toggle${videoOff ? ' active' : ''}`}
                onClick={() => setVideoOff(!videoOff)}
                aria-label={videoOff ? t('call.video_on') : t('call.video_off')}
                aria-pressed={videoOff}
              >
                <span className="material-symbols-outlined">{videoOff ? 'videocam_off' : 'videocam'}</span>
              </button>
            )}

            <button
              type="button"
              className="call-btn call-btn-end"
              onClick={hangup}
              aria-label={t('call.end')}
            >
              <span className="material-symbols-outlined">call_end</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
